import React from "react";
import {
  StyledIconButton,
  PrimaryButton,
} from "../../components/StyledComponents/Buttons/AuradineButtons.js";
import {
  StyledDialog,
  StyledDialogTitle,
} from "../../components/StyledComponents/Dialog/Dialog.js";
import {
  H5HeadlineMedium,
  H8TitleMedium,
  BodyMedium,
} from "../../components/StyledComponents/Typography/Typography.tsx";
import {
  CircularProgress,
  DialogContent,
  FormControl,
  Stack,
  DialogActions,
} from "@mui/material";
import { StyledFormControl } from "../../components/StyledComponents/Inputs/Inputs.js";
import { Controller, useForm } from "react-hook-form";
import CloseIcon from "@mui/icons-material/Close";
import { Box } from "@mui/material";
import {
  StyledTextField,
} from "../../components/StyledComponents/Inputs/Inputs.js";
import useAppContextProvider from "../../AppContext/useAppContextProvider.js";
import { useCaseCommands } from "../queries/useCaseCommands.ts";
import { useTheme } from "../../ThemeContext/ThemeContext.js";

const EscalateCaseModal = (props) => {
  const { open, setOpen, refetch, setTableKey, selectedCases, caseData } = props;
  const { setToastMessage, setToastOpen } = useAppContextProvider();
  const { colors } = useTheme();
  const {
    mutate: sendCaseCommands,
    isPending,
    isSuccess,
  } = useCaseCommands();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isValid },
  } = useForm({
    defaultValues: {
      reason: "",
    },
  });

  const reasonValue = watch("reason");

  const onSubmit = (data) => {
    const { reason } = data;

    // Escalate case command
    sendCaseCommands(
      {
        commands: [
          {
            command: "escalate-case",
            caseIds: selectedCases,
            comment: reason,
          },
        ],
      },
      {
        onSuccess: async (response) => {
          if (response.status === "ok") {
            await refetch();
            setTableKey((prev) => prev + 1);
            setOpen(false);
            setToastMessage(response.msg || "Case escalated successfully");
            setToastOpen(true);
            reset();
          } else {
            setOpen(false);
            setToastMessage(`Error escalating case: ${response.msg || response.Msg || "Unknown error"}`);
            setToastOpen(true);
          }
        },
        onError: (error) => {
          setToastMessage("Error escalating case");
          setToastOpen(true);
        },
      }
    );
  };

  const handleClose = () => {
    setOpen(false);
    reset();
  };

  // Get the selected cases details
  const selectedCasesData = caseData?.filter(caseItem => selectedCases.includes(caseItem.caseId));

  return (
    <StyledDialog
      colors={colors}
      maxWidth={"md"}
      fullWidth
      open={open}
      hideBackdrop
    >
      <StyledDialogTitle sx={{ paddingTop: 0 }}>
        <Box
          display={"flex"}
          flexDirection={"row"}
          justifyContent={"space-between"}
        >
          <H5HeadlineMedium style={{ textAlign: "left" }}>
            Escalate Case
          </H5HeadlineMedium>
          <StyledIconButton
            colors={colors}
            onClick={handleClose}
            style={{ textAlign: "right" }}
          >
            <CloseIcon />
          </StyledIconButton>
        </Box>
      </StyledDialogTitle>
      <DialogContent sx={{ padding: 0 }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box
            display={"flex"}
            flexDirection={"column"}
            gap={8}
          >
            <Box
              display={"flex"}
              flexDirection={"column"}
              gap={4}
              className="escalate-details-container"
            >
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <H8TitleMedium>Escalation Information</H8TitleMedium>
              </Box>

              {/* Display Selected Cases */}
              <Box
                display="flex"
                flexDirection="row"
                flexGrow={1}
              >
                <StyledFormControl colors={colors} fullWidth margin="none">
                  <StyledTextField colors={colors}
                    variant="outlined"
                    label="Selected Cases"
                    fullWidth
                    value={selectedCasesData?.map(caseItem => caseItem?.["Case Number"] || caseItem?.caseNo).join(", ") || "N/A"}
                    disabled
                  />
                </StyledFormControl>
              </Box>

              <Box
                display="flex"
                flexDirection="row"
                flexGrow={1}
              >
                <StyledFormControl colors={colors} fullWidth margin="none">
                  <Controller
                    name="reason"
                    control={control}
                    rules={{
                      required: "Reason for escalation is required",
                      minLength: {
                        value: 3,
                        message: "Reason must be at least 3 characters",
                      },
                      maxLength: {
                        value: 128,
                        message: "Reason must be less than 128 characters",
                      },
                    }}
                    render={({ field }) => (
                      <StyledTextField colors={colors}
                        {...field}
                        variant="outlined"
                        label="Reason to Escalate"
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="Enter the reason for escalating this case..."
                        error={Boolean(errors.reason)}
                        helperText={
                          errors.reason
                            ? errors.reason.message
                            : `Brief reason for escalation (${128 - (reasonValue?.length || 0)} characters remaining)`
                        }
                      />
                    )}
                  />
                </StyledFormControl>
              </Box>

              <BodyMedium>
                <b>Note:</b> Escalating a case will notify higher-level support and may change the case priority.
              </BodyMedium>
            </Box>

            <Box
              display={"flex"}
              flexDirection={"row"}
              justifyContent={"flex-end"}
              gap={4}
              className="action-button-container"
            >
              <PrimaryButton colors={colors}
                type="submit"
                onClick={handleSubmit(onSubmit)}
                disabled={!isValid || isPending}
                endIcon={
                  isPending && (
                    <CircularProgress
                      size={24}
                      sx={{ color: colors.primary.main }}
                    />
                  )
                }
              >
                {isPending
                  ? "Escalating..."
                  : selectedCases.length > 1 ? "Escalate Cases" : "Escalate Case"}
              </PrimaryButton>
            </Box>
          </Box>
        </form>
      </DialogContent>
    </StyledDialog>
  );
};

export default EscalateCaseModal;
